import React, { useState } from "react";
import { PublicLayout } from "../components/PublicLayout";
import { useLightbox } from "../components/Lightbox";

const CATEGORIES = [
  {
    title: "System Architecture",
    images: [
      { src: "/assets/diagrams/diag-system-block.png", title: "System Block Diagram", desc: "ESP32 · sensors · outputs · cloud" },
      { src: "/assets/diagrams/diag-full-block.png", title: "Complete Block Diagram", desc: "Full system with all GPIO pins" },
      { src: "/assets/diagrams/diag-system-realistic.png", title: "Realistic System Overview", desc: "Components with realistic imagery" },
      { src: "/assets/arch-a.png", title: "5-Layer IoT Architecture", desc: "Sensing · controller · connectivity · cloud · UI" },
      { src: "/assets/arch-b.png", title: "Layered Architecture", desc: "Physical · controller · connectivity layers" },
      { src: "/assets/diag-mqtt.png", title: "MQTT Pub/Sub Topology", desc: "Publishers · HiveMQ broker · subscribers" },
      { src: "/assets/diag-usecase.png", title: "Use Case Diagram", desc: "Operator · supervisor · system actors" },
    ],
  },
  {
    title: "Circuit Schematics",
    images: [
      { src: "/assets/diagrams/diag-gpio-pinout.png", title: "ESP32 GPIO Pinout", desc: "All 16 used pins color-coded by function" },
      { src: "/assets/diagrams/diag-tip120-circuits.png", title: "TIP120 Driver Circuits", desc: "Buzzer · fan · light schematics" },
      { src: "/assets/diagrams/diag-tip120-detailed.png", title: "TIP120 Detailed Schematic", desc: "Pinout · specs · flyback diodes" },
      { src: "/assets/diagrams/diag-ds18b20-circuit.png", title: "DS18B20 Temperature Circuit", desc: "1-Wire · 4.7K pull-up · GPIO 5" },
      { src: "/assets/diagrams/diag-sensor-interfaces.png", title: "Sensor Interfaces", desc: "DS18B20 · MQ-135 · PIR · IR receiver" },
      { src: "/assets/diagrams/diag-pzem-closeup.png", title: "PZEM-004T Module Detail", desc: "AC/CT terminals · serial header · measurements" },
      { src: "/assets/arch-c.png", title: "Hardware Block Diagram", desc: "ESP32 DevKit wiring map" },
      { src: "/assets/diag-block.png", title: "Hardware Block (Detailed)", desc: "Metering · sensing · outputs · cloud" },
    ],
  },
  {
    title: "AC Power & Wiring",
    images: [
      { src: "/assets/diagrams/diag-3phase-load.png", title: "3-Phase Load Wiring", desc: "DB board · CTs · PZEM · ESP32 · loads" },
      { src: "/assets/diagrams/diag-ac-wiring-sa.png", title: "SA AC Power & Wiring", desc: "Plug wiring · extension · safety tips" },
      { src: "/assets/diagrams/diag-wire-colors.png", title: "Wire Color Code Guide", desc: "Live · neutral · earth · SA standards" },
      { src: "/assets/diagrams/diag-lamp-holder.webp", title: "E27 Lamp Holder with Switch", desc: "5m cable · thru-switch · SA plug" },
    ],
  },
  {
    title: "Enclosure & Hardware",
    images: [
      { src: "/assets/diagrams/diag-internal-cutaway.jpeg", title: "Internal Cutaway View", desc: "All components inside the enclosure" },
      { src: "/assets/diagrams/diag-enclosure-3d.png", title: "Enclosure 3D Render", desc: "LCD · PIR · gas sensor · buzzer · antenna" },
      { src: "/assets/diagrams/diag-hardware-photo.png", title: "Hardware Studio Photo", desc: "Production-grade enclosure render" },
      { src: "/assets/hw-front.jpeg", title: "Front Panel", desc: "LCD · PIR · camera · Light hub" },
      { src: "/assets/hw-lcd-top.jpeg", title: "Top - 20x4 LCD", desc: "Live V · P · temp readout" },
      { src: "/assets/hw-camera.jpeg", title: "Camera & Flash", desc: "OV2640 lens · LED · vent" },
      { src: "/assets/hw-fan.jpeg", title: "Rear Cooling", desc: "80mm fan · 9V & 12V inputs" },
      { src: "/assets/hw-internal.jpeg", title: "Inside the Build", desc: "ESP32 · PZEM · breadboard" },
      { src: "/assets/hw-panel.png", title: "Field Install", desc: "Wired into a live 3-phase panel" },
    ],
  },
  {
    title: "Bill of Materials & Cost",
    images: [
      { src: "/assets/diagrams/diag-bom-table.png", title: "AC Load Setup BOM", desc: "Lamp holders · LED bulbs · suppliers · prices" },
      { src: "/assets/diagrams/diag-cost-compare.png", title: "Cost Comparison", desc: "R2,348 vs R500,000+ commercial BMS" },
      { src: "/assets/kit-flatlay.png", title: "Component Kit Flatlay", desc: "All components before assembly" },
      { src: "/assets/bom-shopping.jpeg", title: "Enclosure Shopping List", desc: "Materials · glue · where to buy in Cape Town" },
      { src: "/assets/bom-tools.png", title: "Tools & Equipment", desc: "Pricing · quantity · purpose" },
      { src: "/assets/bom-overview.png", title: "Components Overview", desc: "Have · source · still missing" },
      { src: "/assets/doc-procurement.jpeg", title: "Hardware Procurement", desc: "Components · suppliers · pricing" },
      { src: "/assets/bom-cost.jpeg", title: "Bill of Materials", desc: "Cost breakdown · ZAR" },
    ],
  },
  {
    title: "Renders & Concept Art",
    images: [
      { src: "/assets/diagrams/diag-render-1.png", title: "Concept Render 1", desc: "Enclosure design exploration" },
      { src: "/assets/diagrams/diag-render-2.png", title: "Concept Render 2", desc: "Enclosure design exploration" },
      { src: "/assets/diagrams/diag-render-3.png", title: "Concept Render 3", desc: "Enclosure design exploration" },
      { src: "/assets/diagrams/diag-render-4.png", title: "Concept Render 4", desc: "Enclosure design exploration" },
      { src: "/assets/diagrams/diag-poster.png", title: "Project Poster", desc: "Campus Energy Monitor overview" },
    ],
  },
];

const ALL_IMAGES = CATEGORIES.flatMap((c) => c.images);

export default function GalleryPublic() {
  const lb = useLightbox();
  const [filter, setFilter] = useState("All");
  const cats = ["All", ...CATEGORIES.map((c) => c.title)];
  const visible = filter === "All" ? CATEGORIES : CATEGORIES.filter((c) => c.title === filter);

  return (
    <PublicLayout>
      <section className="w-full px-4 sm:px-8 lg:px-12 py-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--accent)]">Gallery</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-2">Hardware & Diagrams</h1>
            <p className="text-[var(--t2)] mt-3">Schematics, circuit diagrams, enclosure renders, wiring guides, and bill of materials. Click any image to view full size.</p>
            <p className="text-xs text-[var(--t2)] font-mono mt-2">{ALL_IMAGES.length} images</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {cats.map((c) => (
              <button key={c} onClick={() => setFilter(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === c ? "bg-[var(--accent)] text-black border-[var(--accent)]" : "bg-[var(--surface2)] border-[var(--bd)] text-[var(--t2)] hover:border-[var(--bd-strong)]"}`}>
                {c}
              </button>
            ))}
          </div>

          {visible.map((cat) => {
            const globalStart = ALL_IMAGES.indexOf(cat.images[0]);
            return (
              <div key={cat.title} className="mb-12">
                <h2 className="font-heading font-bold text-xl sm:text-2xl tracking-tight mb-4">{cat.title}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {cat.images.map((img, i) => (
                    <div key={img.src} onClick={() => lb.open(ALL_IMAGES, globalStart + i)}
                      className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer">
                      <div className="aspect-[4/3] overflow-hidden bg-white">
                        <img src={img.src} alt={img.title} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" />
                      </div>
                      <div className="p-3">
                        <h3 className="font-heading font-bold text-xs sm:text-sm truncate">{img.title}</h3>
                        <p className="text-[10px] text-[var(--t2)] mt-0.5 font-mono truncate">{img.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      {lb.node}
    </PublicLayout>
  );
}
